import React, { FC, Fragment, useMemo } from 'react';
import { Grid, Box, Divider, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { liveOrderCountsContext } from 'apollo/order-live-updates';
import { workflowOverviewProductionTemplate } from 'apollo/templates/workFlow';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import PageTitle from 'components/shared/page-title/page-title.component';
import StepBox from './step-box.component';
import StaticStepBox from './static-step-box.component';
import { OverView, ProductionOverView } from 'models/workflow-step';
import { Department } from 'models/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { permissions } from 'utils/permission';

type DepartmentGroup = {
    name: string;
    steps: { step: OverView; stepNumber: number }[];
};

type ProductionSectionProps = {
    departments: Department[];
    departmentsLoading: boolean;
    hiddenDepartments: Set<string>;
};

const ProductionSection: FC<ProductionSectionProps> = ({ departments, departmentsLoading, hiddenDepartments }) => {
    const { t: translate } = useTranslation(['workflow']);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const isExternalTechnician = permissions.isExternalTechnician(currentUser.role);

    const { loading: loadingProduction, data: result } = useQuery(workflowOverviewProductionTemplate, {
        context: liveOrderCountsContext,
        fetchPolicy: 'network-only',
        onError(error) {
            showBoundary(error);
        },
    });
    // Registered as a count view: an ORDER_CHANGED refresh (DD-3231) re-reads it into the cache.
    const data: ProductionOverView = result?.workflowOverviewProduction ?? ({} as ProductionOverView);

    const visibleStepOrders = useMemo(() => {
        const stepOrders = data?.stepOrders || [];
        if (!isExternalTechnician) return stepOrders;

        const orgSteps = currentOrg.workFlow?.steps || [];
        const sharedStepIds = new Set(
            orgSteps
                .filter((step) => step.defaultExtUsers?.some((user) => user._id === currentUser._id))
                .map((step) => step._id)
                .filter((id): id is string => Boolean(id)),
        );
        return stepOrders.filter((step) => sharedStepIds.has(step.stepId));
    }, [data?.stepOrders, isExternalTechnician, currentUser._id, currentOrg.workFlow?.steps]);

    const departmentGroups = useMemo((): DepartmentGroup[] => {
        const stepOrders = visibleStepOrders;

        if (!stepOrders.length) return [];

        const stepToDepartment = new Map<string, string>();
        for (const dept of departments) {
            for (const ws of dept.workflowSteps) {
                stepToDepartment.set(ws._id, dept.name);
            }
        }

        const groupMap = new Map<string, { step: OverView; stepNumber: number }[]>();
        stepOrders.forEach((step, index) => {
            const deptName = stepToDepartment.get(step.stepId) || '';
            if (!groupMap.has(deptName)) {
                groupMap.set(deptName, []);
            }
            groupMap.get(deptName)!.push({ step, stepNumber: index + 1 });
        });

        const result: DepartmentGroup[] = [];
        for (const dept of departments) {
            const steps = groupMap.get(dept.name);
            if (steps?.length) {
                result.push({ name: dept.name, steps });
            }
        }

        const noDeptSteps = groupMap.get('');
        if (noDeptSteps?.length) {
            result.push({ name: '', steps: noDeptSteps });
        }

        return result;
    }, [data, departments]);

    const visibleGroups = useMemo(
        () => departmentGroups.filter((g) => !hiddenDepartments.has(g.name)),
        [departmentGroups, hiddenDepartments],
    );

    const hasDepartments = visibleGroups.length > 0 && visibleGroups.some((g) => g.name !== '');
    const loading = loadingProduction || departmentsLoading;

    return (
        <Box mb={4}>
            <PageTitle headingStyle="h2" title={translate('PRODUCTION')} />
            {loading ? (
                <Grid container spacing={1}>
                    {[...Array(8)].map((_, idx) => (
                        <Grid item md={3} key={idx}>
                            <Skeleton variant="rectangular" height={130} />
                        </Grid>
                    ))}
                </Grid>
            ) : hasDepartments ? (
                <>
                    <Grid container spacing={1} mb={2}>
                        <Grid item md={3}>
                            <StaticStepBox data={data?.noStep} title={translate('workflow:NO_STEP')} filters={{}} />
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 4 }} />
                    {visibleGroups.map((group, groupIndex) => (
                        <Fragment key={group.name || '__no_dept'}>
                            {groupIndex > 0 && <Divider sx={{ my: 4 }} />}
                            <Box display="flex" mb={2}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: 32,
                                        mr: 1,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            writingMode: 'vertical-rl',
                                            transform: 'rotate(180deg)',
                                            whiteSpace: 'nowrap',
                                            letterSpacing: '0.05em',
                                        }}
                                    >
                                        {group.name || translate('workflow:NO_DEPARTMENT')}
                                    </Typography>
                                </Box>
                                <Grid container spacing={1} flex={1}>
                                    {group.steps.map(({ step }) => (
                                        <Grid item md={3} key={step.stepId}>
                                            <StepBox step={step} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Fragment>
                    ))}
                </>
            ) : (
                <Grid container spacing={1}>
                    <Grid item md={3}>
                        <StaticStepBox data={data?.noStep} title={translate('workflow:NO_STEP')} filters={{}} />
                    </Grid>
                    {visibleStepOrders.map((step, index) => (
                        <Grid item md={3} key={index}>
                            <StepBox step={step} />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default ProductionSection;
